import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@xxtract/ui';
import type { OverkoepelendAggregate, WerkprocesFunnel } from '../processes.api';

/**
 * Lens 1 (overkoepelende werkproces-band) — vijf tellers + de werkproces-funnel over álle diensten
 * samen. Elk product telt één keer, in de fase waar het het verst achterloopt; PHYSICAL (de aparte
 * fysieke lens) telt hier niet mee. De vier fasetellers samen zijn de verschillende producten;
 * stilstand is een overlay over die fasen en staat daarom achteraan (spec-processen-actuele-stand
 * CAP-10/11).
 */
type Teller = keyof Pick<OverkoepelendAggregate, 'delivered' | 'running' | 'notStarted' | 'unknownStep' | 'stagnating'>;
const TELLERS: readonly { key: Teller; tone: string; hint?: string; span?: string }[] = [
  { key: 'delivered', tone: 'text-[var(--success-dark)]' },
  { key: 'running', tone: 'text-[var(--secondary)]' },
  { key: 'notStarted', tone: 'text-[var(--foreground-muted)]' },
  { key: 'unknownStep', tone: 'text-[var(--foreground-muted)]', hint: 'processes.band.unknownStepHint' },
  // Op een smal scherm staat stilstand als vijfde alleen op een rij; dan over de volle breedte.
  { key: 'stagnating', tone: 'text-[var(--destructive)]', span: 'col-span-2 sm:col-span-1' },
];

const FUNNEL_STAGES: readonly (keyof WerkprocesFunnel)[] = ['intake', 'verwerking', 'kwaliteit', 'oplevering'];

/** Every figure counts running work, each product once, and follows the filter (spec-processen-actuele-stand CAP-5/6/10). */
export function OverkoepelendBand({ overview }: { overview: OverkoepelendAggregate }) {
  const { t } = useTranslation();

  return (
    <section className="space-y-3" data-testid="processes-overkoepelend-band">
      <h2 className="text-sm font-semibold text-[var(--foreground-muted)]">{t('processes.lens.overkoepelend')}</h2>
      <Card>
        <CardContent className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {TELLERS.map(({ key, tone, hint, span }) => (
              <div className={`rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] p-3 ${span ?? ''}`} data-testid={`band-teller-${key}`} key={key}>
                <p className={`text-2xl font-bold ${tone}`}>{overview[key]}</p>
                <p className="text-[11px] text-[var(--foreground-muted)]">{t(`processes.band.${key}`)}</p>
                {hint ? <p className="mt-1 text-[10px] leading-snug text-[var(--foreground-muted)]">{t(hint)}</p> : null}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="processes-werkproces-funnel">
            {FUNNEL_STAGES.map((stage) => (
              <div className="rounded-[var(--radius-control)] bg-[var(--background-muted)] p-2 text-center" data-testid={`funnel-stage-${stage}`} key={stage}>
                <p className="text-lg font-semibold text-[var(--foreground)]">{overview.funnel[stage]}</p>
                <p className="text-[11px] text-[var(--foreground-muted)]">{t(`processes.stage.${stage}`)}</p>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-[var(--foreground-muted)]" data-testid="band-stagnation-note">
            {t('processes.band.stagnationNote', { count: overview.stagnationThresholdWorkdays })}
          </p>
          {overview.ordersTruncated && (
            <p className="text-[11px] text-[var(--info-text)]" data-testid="band-orders-truncated">{t('processes.ordersTruncated.band')}</p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
